import { Component, ElementRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DomSanitizer } from '@angular/platform-browser';
import Dygraph from 'dygraphs';
import { find } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { EmissionLine } from '../enums/emission-line-type';
import { VantaResultExportType } from '../enums/vanta-result-export-type';
import { ExportService } from '../export.service';
import { IChemistryFormattedItem } from '../interfaces/chemistry-formatted-item';
import { IVantaDisplayShotSetting } from '../interfaces/display-settings';
import { IResultFormattedItem } from '../interfaces/result-formatted-item';
import { IShotGradeSpec } from '../interfaces/shot-grade-spec';
import { SelectionModalComponent } from '../selection-modal/selection-modal.component';
import { DataService } from '../services/data.service';
import { SettingsService } from '../services/settings.service';
import { SpectrumService } from '../services/spectrum.service';
import { SearchResultItem } from './../interfaces/search-result-item';
import { LoaderService } from '@evc/platform';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../material';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DataManagerBannerComponent } from '../data-manager-banner/data-manager-banner.component';
import { GradeBarComponent } from '../grade-bar/grade-bar.component';
import { IconComponent } from '@evc/web-components';

@Component({
    selector: 'app-vanta-shot-result',
    templateUrl: 'vanta-shot-result.component.html',
    styleUrls: ['./vanta-shot-result.component.scss'],
    imports: [CommonModule, MaterialModule, FormsModule, ReactiveFormsModule, DataManagerBannerComponent, GradeBarComponent, IconComponent]
})
export class VantaShotResultComponent implements OnInit {
  readonly loader = inject(LoaderService);
  constructor(
    private dataService: DataService,
    private dialog: MatDialog,
    private spectrumService: SpectrumService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private settingsService: SettingsService,
    private toastService: ToastrService,
    private exportService: ExportService
  ) {
    this.iconRegistry.addSvgIcon(
      'periodic',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/img/periodic.svg')
    );
    this.iconRegistry.addSvgIcon(
      'energy',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/img/energy.svg')
    );
  }
  public data: Array<Array<number>>;
  public context: CanvasRenderingContext2D;
  public options: dygraphs.Options = {
    labels: ['x'],
    legend: 'always',
    xlabel: 'Energy',
    ylabel: 'Count',
    animatedZooms: false,
    pointSize: 4,
  };
  private graphHandle: Dygraph;

  public gettingShot = false;
  public showBlade = false;
  public showChart = false;
  public showBanner = false;
  public infoShowBlade: any = null;

  public selectedGrade: any;
  public gradeDisplayString = '';
  public selectedGradeIndex = 1;

  public displayedColumns = ['elementName', 'concentration', 'error', 'grade'];
  public displayedColumns2 = ['elementName', 'concentration', 'error'];
  public displayedColumnNames: any = {};
  public displayedColumnNames2: any = {};
  public tableDataSource: any;
  public tableDataSource2: any;
  public calcAnno: any = {};

  private atomicMap: any = {
    H: 1,
    He: 2,
    Li: 3,
    Be: 4,
    B: 5,
    C: 6,
    N: 7,
    O: 8,
    F: 9,
    Ne: 10,
    Na: 11,
    Mg: 12,
    Al: 13,
    Si: 14,
    P: 15,
    S: 16,
    Cl: 17,
    Ar: 18,
    K: 19,
    Ca: 20,
    Sc: 21,
    Ti: 22,
    V: 23,
    Cr: 24,
    Mn: 25,
    Fe: 26,
    Co: 27,
    Ni: 28,
    Cu: 29,
    Zn: 30,
    Ga: 31,
    Ge: 32,
    As: 33,
    Se: 34,
    Br: 35,
    Kr: 36,
    Rb: 37,
    Sr: 38,
    Y: 39,
    Zr: 40,
    Nb: 41,
    Mo: 42,
    Tc: 43,
    Ru: 44,
    Rh: 45,
    Pd: 46,
    Ag: 47,
    Cd: 48,
    In: 49,
    Sn: 50,
    Sb: 51,
    Te: 52,
    I: 53,
    Xe: 54,
    Cs: 55,
    Ba: 56,
    La: 57,
    Ce: 58,
    Pr: 59,
    Nd: 60,
    Pm: 61,
    Sm: 62,
    Eu: 63,
    Gd: 64,
    Tb: 65,
    Dy: 66,
    Ho: 67,
    Er: 68,
    Tm: 69,
    Yb: 70,
    Lu: 71,
    Hf: 72,
    Ta: 73,
    W: 74,
    Re: 75,
    Os: 76,
    Ir: 77,
    Pt: 78,
    Au: 79,
    Hg: 80,
    Tl: 81,
    Pb: 82,
    Bi: 83,
    Po: 84,
    At: 85,
    Rn: 86,
    Fr: 87,
    Ra: 88,
    Ac: 89,
    Th: 90,
    Pa: 91,
    U: 92,
    Np: 93,
    Pu: 94,
    Am: 95,
    Cm: 96,
    Bk: 97,
    Cf: 98,
    Es: 99,
    Fm: 100,
    Md: 101,
    No: 102,
    Lr: 103,
    Rf: 104,
    Db: 105,
    Sg: 106,
    Bh: 107,
    Hs: 108,
    Mt: 109,
    Ds: 110,
    Rg: 111,
    Cn: 112,
    Nh: 113,
    Fl: 114,
    Mc: 115,
    Lv: 116,
    Ts: 117,
    Og: 118,
  };

  public optionMaps: any = {
    zoom: 2,
    latitude: 0,
    longitude: 0,
    minZoom: 2,
  };
  public timeTestTaken = new Date();

  public displaySettings: IVantaDisplayShotSetting;
  public nSigmaVal = [1, 2, 3];
  public numMatch = [1, 2, 3, 4];
  public pseudoElements = ['None', 'Si', 'Model1'];
  public auKaratOptionsVal = ['None', 'Fineness', 'AuKarat'];
  public auKaratDecimalVal = [0, 1, 2];
  public auKaratSelected: string;
  public auKaratDecimalSelected: number;
  public userFactors = ['Ni'];
  public showEstimatedLod: boolean;
  public hideChemistry: boolean;
  public showUncertainty: boolean;
  public showPlateAlert: boolean;
  public showQuantityAbove10: boolean;
  public showUserFactorName: boolean;
  public shotData: any;
  public isShowQuantityAbove10SettingVisible: boolean;
  public isShowPlateAlertSettingVisible: boolean;
  public isShowAuKaratSettingVisible: boolean;


  @Input()
  public searchResultItem: SearchResultItem[];
  @Input() public searching = false;
  @ViewChild('dataTabs') public dataTabs: MatTabGroup;    
  @ViewChild('sort1') sort1!: MatSort;
  @ViewChild('sort2') sort2!: MatSort;
  @ViewChild('chart') public chart: ElementRef;

  public updateBlade(selectTab?: number) {
    this.showBanner = this.searchResultItem.length === 0;
    if (this.searchResultItem && this.searchResultItem.length === 1) {
      if (this.searchResultItem[0].testedDateUtc) {
        this.timeTestTaken = new Date(this.searchResultItem[0].testedDateUtc);
      }
      this.showBladeData(this.searchResultItem[0]);
    }
    setTimeout(() => {
      if (selectTab) {
        this.dataTabs.selectedIndex = selectTab;
      } else {
        if (this.searchResultItem.length === 1) {
          this.dataTabs.selectedIndex = 0;
        } else if (this.searchResultItem.length > 1) {
          this.dataTabs.selectedIndex = 3;
        }
      }
    }, 10);
  }

  public ngOnInit() {
    this.options.zoomCallback = function(minDate, maxDate, yRanges) {
      if (this.isZoomed('y')) {
        this.updateOptions({ valueRange: null });
        return;
      }
    };
    this.updateBlade();
  }

  public onDisplayShotSettingChange(
    displayOption: any,
    change: MatCheckboxChange
  ) {
    switch (displayOption) {
      case 'showEstimatedLod': {
        this.showEstimatedLod = change.checked;
        this.displaySettings.methodDisplayOptions.showEstimatedLod = change.checked
          ? 1
          : -1;
        break;
      }
      case 'showUncertainty': {
        this.showUncertainty = change.checked;
        this.displaySettings.methodDisplayOptions.showUncertainty = change.checked
          ? 1
          : -1;
        break;
      }
      case 'hideChemistry': {
        this.hideChemistry = change.checked;
        this.displaySettings.methodDisplayOptions.hideChemistry = change.checked
          ? 1
          : -1;
        break;
      }
      case 'showUserFactorName': {
        this.showUserFactorName = change.checked;
        this.displaySettings.methodDisplayOptions.showUserFactorName = change.checked
          ? 1
          : -1;
        break;
      }
      case 'showPlateAlert': {
        this.showPlateAlert = change.checked;
        this.displaySettings.methodDisplayOptions.showPlateAlert = change.checked
          ? 1
          : -1;
        break;
      }
      case 'showQuantityAbove10': {
        this.showQuantityAbove10 = change.checked;
        this.displaySettings.methodDisplayOptions.showQuantityAbove10 = change.checked
          ? 1
          : -1;
        break;
      }
      default: {
        break;
      }
    }

    this.updateSettings();
  }

  public onDisplayShotSettingSelectionChange() {
    if (this.auKaratSelected === 'Fineness') {
      this.displaySettings.methodDisplayOptions.showAuKaratDecimal = -1;
      this.displaySettings.methodDisplayOptions.showAuKaratOrFineness = 1;
    } else if (this.auKaratSelected === 'AuKarat') {
      this.displaySettings.methodDisplayOptions.showAuKaratDecimal = this.auKaratDecimalSelected;
      this.displaySettings.methodDisplayOptions.showAuKaratOrFineness = 2;
    } else {
      this.displaySettings.methodDisplayOptions.showAuKaratDecimal = -1;
      this.displaySettings.methodDisplayOptions.showAuKaratOrFineness = -1;
    }

    this.updateSettings();
  }

  public showBladeData(searchResultItem: SearchResultItem): void {
    if (searchResultItem !== null && searchResultItem !== undefined) {
      this.showBlade = true;
      this.showChart = true;
      this.gettingShot = true;
      this.loader.play();
      this.settingsService
        .getSettings()
        .then((settings: IVantaDisplayShotSetting) => {
          this.displaySettings = settings;
          this.showEstimatedLod =
            settings.methodDisplayOptions.showEstimatedLod > 0;
          this.hideChemistry = settings.methodDisplayOptions.hideChemistry > 0;
          this.showUncertainty =
            settings.methodDisplayOptions.showUncertainty > 0;
          this.showPlateAlert =
            settings.methodDisplayOptions.showPlateAlert > 0;
          this.showQuantityAbove10 =
            settings.methodDisplayOptions.showQuantityAbove10 > 0;
          this.showUserFactorName =
            settings.methodDisplayOptions.showUserFactorName > 0;

          if (
            settings.methodDisplayOptions.showAuKaratOrFineness === 1 &&
            settings.methodDisplayOptions.showAuKaratDecimal < 0
          ) {
            this.auKaratSelected = 'Fineness';
          } else if (
            settings.methodDisplayOptions.showAuKaratOrFineness === 2 &&
            settings.methodDisplayOptions.showAuKaratDecimal >= 0
          ) {
            this.auKaratSelected = 'AuKarat';
          } else {
            this.auKaratSelected = 'None';
          }

          if (settings.methodDisplayOptions.showAuKaratDecimal < 0) {
            this.auKaratDecimalSelected = 0;
          } else {
            this.auKaratDecimalSelected =
              settings.methodDisplayOptions.showAuKaratDecimal;
          }
        });
      this.dataService.getVantaShot(searchResultItem.id).subscribe(
        (shot: IResultFormattedItem) => {
          if ((shot.data.tableDataDetectedArray || []).length > 0) {
            this.infoShowBlade = searchResultItem;
            this.infoShowBlade.gradeList = [];
            this.infoShowBlade.gradeList = shot.data.gradeMatch.filter(
              (itm) => {
                if (itm.passFailShowMatchNumber) {
                  this.infoShowBlade.passFailGrade = itm;
                }
                return itm.compGrade === 1 && !itm.passFailShowMatchNumber;
              }
            );
            this.infoShowBlade.gradeList = this.infoShowBlade.gradeList.map(
              (itm) => {
                const resItm = shot.data.resultFormattedData.find(
                  (resultItm) => {
                    return resultItm.raw === itm.gradeName;
                  }
                );
                if (resItm) {
                  itm.displayStr = resItm.displayStr;
                } else {
                  itm.displayStr = '';
                }
                return itm;
              }
            );
            if (this.infoShowBlade.gradeList.length > 0) {
              this.selectedGrade = this.infoShowBlade.gradeList[0];
              this.gradeDisplayString = this.selectedGrade.displayStr;
              this.selectedGradeIndex = 1;
            } else {
              this.selectedGrade = null;
            }
            this.infoShowBlade.gradeMatch = shot.data.gradeMatch;
            this.infoShowBlade.displayStrings = [];
            this.infoShowBlade.displayStrings = shot.data.resultFormattedData.filter(
              (itm) => {
                if (itm.isPassFailGrade) {
                  this.infoShowBlade.passFailGrade.displayStr = itm.displayStr;
                }
                if (itm.displayStr && itm.displayStr.toLowerCase().startsWith('userfactorname')) {
                    itm.displayStr = '';
                }
                return (
                  !itm.isCompareGrade &&
                  !itm.isExactGradeMatch &&
                  !itm.isGradeMatch &&
                  !itm.isPassFailGrade
                );
              }
            );
            this.infoShowBlade.resultFormattedData =
              shot.data.resultFormattedData;
            this.infoShowBlade.gradeSpec =
              shot.data.gradeMatch.length === 0
                ? []
                : shot.data.gradeMatch[0].gradeSpec;
            this.infoShowBlade.chemistry = shot.data.chemistry;
            this.buildTableData(shot.data.tableDataDetectedArray[0]);
            this.buildLODTableData(shot.data.tableDataNonDetectedArray[0]);
            this.infoShowBlade.matchNumber =
              shot.data.gradeMatch.length === 0
                ? ''
                : shot.data.gradeMatch[0].matchNumber === 0
                ? 'Exact'
                : Math.floor(shot.data.gradeMatch[0].matchNumber * 100) / 100;
            this.infoShowBlade.gradeMatchInfo = shot.data.gradeMatch;
            this.infoShowBlade.spectra = shot.data.spectra;
            this.infoShowBlade.tableDataDetectedArray = shot.data.tableDataDetectedArray;
            this.infoShowBlade.tableDataNonDetectedArray = shot.data.tableDataNonDetectedArray;
            this.infoShowBlade.analysis = shot.data.analysis;

            this.infoShowBlade.notes = null;
            if (shot.data.testInfo !== null) {
                this.infoShowBlade.notes = {};
                Object.keys(shot.data.testInfo).forEach(itm => {
                    this.infoShowBlade.notes[itm] =
                        shot.data.testInfo[itm] ?? '';                 
                });
            }

            this.shotData = shot;
            this.isShowQuantityAbove10SettingVisible = shot.data.analysis.methodDispName.toLowerCase().startsWith('rohs');
            this.isShowPlateAlertSettingVisible = shot.data.analysis.methodId.toLowerCase().startsWith('preciousmetal');
            this.isShowAuKaratSettingVisible = shot.data.analysis.methodId.toLowerCase().startsWith('preciousmetal');             

          } else {
            this.infoShowBlade = searchResultItem;
            this.infoShowBlade.chemistry = [];
            this.tableDataSource = [];
          }
          if ((shot.data.spectra || []).length > 0) {
            this.buildChart(shot);
          }
          this.gettingShot = false;
          this.loader.stop();
        },
        (err) => {
          this.loader.stop();
          this.toastService.error(
            `Couldn't load the result. Try again with other result.`
          );
        }
      );
    } else {
      this.closeBlade();
    }
  }

  public closeBlade(): void {
    this.showBlade = false;
    this.showChart = false;
    this.showBanner = true;
    this.infoShowBlade = null;
  }

  public buildTableData(tableData: IChemistryFormattedItem) {
    const dataSource = new Array<any>();
    this.processTableData(tableData, dataSource);    
    this.tableDataSource = new MatTableDataSource(dataSource);
    setTimeout(() => {
        this.tableDataSource.sort = this.sort1;
        this.tableDataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'concentration': {
                    return item.concentration.raw;
                }
                case 'error': {
                    return item.error.raw;
                }
                case 'elementName': {
                    return item.elementName.raw;
                }
                default: {
                    return item[property];
                }
            }
        };

        this.sort1.disableClear = true;         
        this.sort1.active = 'concentration';    
        this.sort1.direction = 'desc';          
        this.sort1.sortChange.emit({
            active: this.sort1.active,
            direction: this.sort1.direction
        });
    });
  }

  private parseLodSortableValue(value: any): number {
    if (!value || !value.display) {
        return Number.POSITIVE_INFINITY;
    }

    const display = value.display.toString().trim();
    
    if (display === 'ND') {
        return Number.POSITIVE_INFINITY;
    }
    
    if (display.startsWith('<')) {
        const parsed = parseFloat(display.substring(1));
        return isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
    }
    
    const raw = Number(value.raw);
    return isNaN(raw) ? Number.POSITIVE_INFINITY : raw;
  }


  public buildLODTableData(tableData: IChemistryFormattedItem) {
    const dataSource2 = new Array<any>();
    this.processLODTableData(tableData, dataSource2);
    this.tableDataSource2 = new MatTableDataSource(dataSource2);
    setTimeout(() => {
        this.tableDataSource2.sort = this.sort2;
        this.tableDataSource2.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'error':                    
                    return this.parseLodSortableValue(item.error);

                case 'elementName':
                    return item.elementName?.raw ?? '';

                case 'concentration':                    
                    return item.concentration?.raw ?? Number.POSITIVE_INFINITY;

                default:
                    return Number.POSITIVE_INFINITY;
            }
        };
    });
  }

  private processTableData(tableData: IChemistryFormattedItem, dataSource: any[]) {
    this.displayedColumnNames = tableData.columnHeader;
    this.displayedColumns = tableData.columnHeader.map((itm) => {
        if (itm.includes('El')) {
            this.displayedColumnNames.elementName = itm;
            return 'elementName';
        }
        else if (itm.includes('%') ||
            itm.includes('PPM') ||
            itm.includes('Layer')) {
            this.displayedColumnNames.concentration = itm;
            return 'concentration';
        }
        else if (itm.includes('+/-')) {
            this.displayedColumnNames.error = itm;
            return 'error';
        }
        else {
            this.displayedColumnNames.grade = itm;
            return 'grade';
        }
    });
    tableData.rowData.forEach((itm) => {
        const gradeSpec = this.GetShotGradeSpec(
            itm.cellData[0].display.trim(),
            tableData.rowData
        );
        dataSource.push({
            elementName: itm.cellData[0],
            outOfSpec: gradeSpec
                ? itm.cellData[1].raw >= 0 &&
                (itm.cellData[1].raw < gradeSpec.minVal ||
                    itm.cellData[1].raw > gradeSpec.maxVal)
                : false,
            concentration: itm.cellData[1],
            error: itm.cellData[2],
            showGrade: itm.cellData[3]
                ? itm.cellData[3].display.includes(',')
                : false,
            grade: itm.cellData[3]
                ? !itm.cellData[3].display.includes(',')
                    ? itm.cellData[3].display.trim()
                    : gradeSpec
                : '',
            color: itm.color,
        });
    });
  }

  private processLODTableData(tableData: IChemistryFormattedItem, dataSource: any[]) {
    tableData.columnHeader.splice(3, 1);
    this.displayedColumnNames2 = tableData.columnHeader;
    this.displayedColumns2 = tableData.columnHeader.map((itm) => {
        if (itm.includes('El')) {
            this.displayedColumnNames2.elementName = itm;
            return 'elementName';
        }
        else if (itm.includes('%') ||
            itm.includes('PPM') ||
            itm.includes('Layer')) {
            this.displayedColumnNames2.concentration = itm;
            return 'concentration';
        }
        else if (itm.includes('+/-')) {
            this.displayedColumnNames2.error = itm;
            return 'error';
        }            
    });
    tableData.rowData.forEach((itm) => {
        const gradeSpec = this.GetShotGradeSpec(
            itm.cellData[0].display.trim(),
            tableData.rowData
        );
        dataSource.push({
            elementName: itm.cellData[0],
            outOfSpec: gradeSpec
                ? itm.cellData[1].raw >= 0 &&
                (itm.cellData[1].raw < gradeSpec.minVal ||
                    itm.cellData[1].raw > gradeSpec.maxVal)
                : false,
            concentration: itm.cellData[1],
            error: itm.cellData[2],
            showGrade: itm.cellData[3]
                ? itm.cellData[3].display.includes(',')
                : false,                
            color: itm.color,
        });
    });
  }

  public GetShotGradeSpec(
    elementName: string,
    tableData: Array<any>
  ): IShotGradeSpec {
    let gradeSpec: any;
    if (this.selectedGrade) {
      gradeSpec = find(
        this.selectedGrade.gradeSpec,
        (o) => o.elementName === elementName
      );
    }

    if (gradeSpec !== undefined) {
      const chemistry = find(tableData, (o) =>
        o.cellData[0].display === elementName
      );
      const shotGradeSpec: IShotGradeSpec = {
        concentration: chemistry.cellData[1].raw,
        error: chemistry.cellData[2].raw,
        maxVal: gradeSpec.maxVal,
        minVal: gradeSpec.minVal,
        rawData: chemistry.cellData[3].display.split(',')
      };

      return shotGradeSpec;
    }

    return null;
  }

  public onGradeChanged(event, skip: boolean) {
    if (!skip) {
      this.gradeDisplayString = event.displayStr;
      this.selectedGradeIndex = this.infoShowBlade.gradeList.indexOf(event) + 1;
    }
    if (
      this.displayedColumns.length === 4 &&
      this.displayedColumnNames.grade !== event.gradeName
    ) {
      this.buildTableData(
        (this.infoShowBlade.tableDataDetectedArray as Array<any>).find(
          (itm) => {
            return itm.columnHeader[3] === event.gradeName;
          }
        )
      );
    }
  }

  // CHART FUNCTIONS
  public resetChart() {
    this.graphHandle.resetZoom();
  }

  public selectElements() {
    const anno = this.graphHandle.annotations();
    const dialogRef = this.dialog.open(SelectionModalComponent, {
      data: {
        title: 'Select Elements',
        list: Object.keys(this.atomicMap).map((itm) => ({
          name: itm,
          selected: this.calcAnno[itm]
            ? this.calcAnno[itm].ka.display &&
              this.calcAnno[itm].kb.display &&
              this.calcAnno[itm].la.display &&
              this.calcAnno[itm].lb.display
            : false,
        })),
      },
    });
    dialogRef.afterClosed().subscribe((res: Array<any>) => {
      if (res === undefined) {
        return;
      }
      const newAnno: dygraphs.Annotation[] = [];
      this.resetEnergyLines();
      res.forEach((itm) => {
        if (!this.calcAnno[itm]) {
          this.calcAnno[itm] = { la: {}, lb: {}, ka: {}, kb: {} };
          let KAlpha = this.spectrumService.emissionLineEnergy(
            this.atomicMap[itm],
            EmissionLine.KAlpha
          );
          let KBeta = this.spectrumService.emissionLineEnergy(
            this.atomicMap[itm],
            EmissionLine.KBeta
          );
          let LAlpha = this.spectrumService.emissionLineEnergy(
            this.atomicMap[itm],
            EmissionLine.LAlpha
          );
          let LBeta = this.spectrumService.emissionLineEnergy(
            this.atomicMap[itm],
            EmissionLine.LBeta
          );
          if ((KAlpha * 100) % 2 !== 0) {
            KAlpha = (KAlpha * 100 + 1) / 100;
          }
          if ((KBeta * 100) % 2 !== 0) {
            KBeta = (KBeta * 100 + 1) / 100;
          }
          if ((LAlpha * 100) % 2 !== 0) {
            LAlpha = (LAlpha * 100 + 1) / 100;
          }
          if ((LBeta * 100) % 2 !== 0) {
            LBeta = (LBeta * 100 + 1) / 100;
          }
          this.calcAnno[itm].ka = {
            display: true,
            anno: this.getAnnotation(KAlpha, itm + 'Kα'),
          };
          this.calcAnno[itm].kb = {
            display: true,
            anno: this.getAnnotation(KBeta, itm + 'Kß'),
          };
          this.calcAnno[itm].la = {
            display: true,
            anno: this.getAnnotation(LAlpha, itm + 'Lα'),
          };
          this.calcAnno[itm].lb = {
            display: true,
            anno: this.getAnnotation(LBeta, itm + 'Lß'),
          };
        } else {
          this.calcAnno[itm].ka.display = true;
          this.calcAnno[itm].kb.display = true;
          this.calcAnno[itm].la.display = true;
          this.calcAnno[itm].lb.display = true;
        }
      });
      this.updateAnnotations(newAnno);
    });
  }

  public selectLines() {
    const dialogRef = this.dialog.open(SelectionModalComponent, {
      data: {
        title: 'Select Element lines',
        list: this.getEnergyLines(),
      },
    });
    dialogRef.afterClosed().subscribe((res: Array<any>) => {
      if (res === undefined) {
        return;
      }
      const newAnno: dygraphs.Annotation[] = [];
      this.resetEnergyLines();
      res.forEach((itm: string) => {
        const elementInfo = itm.split(' ');
        if (elementInfo[1] === 'Kα') {
          this.calcAnno[elementInfo[0]].ka.display = true;
        }
        if (elementInfo[1] === 'Kß') {
          this.calcAnno[elementInfo[0]].kb.display = true;
        }
        if (elementInfo[1] === 'Lα') {
          this.calcAnno[elementInfo[0]].la.display = true;
        }
        if (elementInfo[1] === 'Lß') {
          this.calcAnno[elementInfo[0]].lb.display = true;
        }
      });
      this.updateAnnotations(newAnno);
    });
  }

  public print() {
    window.print();
  }

  public buildChart(shot: any): any {
    try {
      if (this.graphHandle) {
        this.graphHandle.destroy();
        this.graphHandle = undefined;
      }
      this.formatSpectraData(shot);
      this.showChart = true;
    } catch (e) {
      console.log(e);
    }
  }

  // Format spectra data for vanta-shot-result-component chart
  public formatSpectraData(shot: any): any {
    // Return case
    const spectra = shot.data.spectra;
    if (spectra.length < 1) {
      return;
    }

    this.data = [];
    this.options.labels = ['x'];
    for (let j = 1; j <= shot.data.spectra[0].data.length; j++) {
      const val =
        Math.round(
          (j * shot.data.spectra[0].energySlope +
            shot.data.spectra[0].energyOffset) *
            100
        ) / 100;
      this.data.push([val]);
    }
    for (let i = 0; i < shot.data.spectra.length; i++) {
      const dataset = shot.data.spectra[i].data.map((res: any) => res);
      this.options.labels.push('Beam-' + (i + 1));
      dataset.forEach((element, index) => {
        this.data[index].push(element);
      });
    }
  }


  public tabChanged(tab: MatTabChangeEvent) {
  if (tab.index === 1 && this.graphHandle === undefined) {
    setTimeout(() => {
        this.graphHandle = new Dygraph(
          this.chart.nativeElement,
          this.data,
          this.options
        );
      setTimeout(() => {
        this.graphHandle?.resize();
      }, 300);
    }, 150);
  }
}

  public exportVantaShotCSV() {
      this.loader.play();
      const shotIds: string[] = [];
      shotIds.push(this.infoShowBlade.id);
      this.dataService
      .exportVantaShot(shotIds, VantaResultExportType.CsvExport)
      .subscribe(
        (data) => {
          this.loader.stop();
          this.exportService.exportVantaShotCSV(`${this.infoShowBlade.id}.zip`, data);
        },
        (err) => {
          this.loader.stop();
          this.toastService.error(`Couldn't export the result.`);
        }
      );
  }
  public exportVantaShotJSON() {
    this.exportService.exportVantaShotJSON(`${this.infoShowBlade.id}.json`, this.shotData);
  }

  private getTopBeam(xVal: number): string {
    let beamIndex = this.data
      .find((itm) => {
        return itm[0] >= xVal;
      })
      .reduce(
        (indexMax, currentIndex, index, arr) =>
          currentIndex > arr[indexMax] ? index : indexMax,
        0
      );
    if (beamIndex <= 0) {
      beamIndex = 1;
    }
    if (beamIndex < this.options.labels.length) {
      return this.options.labels[beamIndex];
    }
    return '';
  }

  private getAnnotation(energy: number, name: string): dygraphs.Annotation {
    return {
      series: this.getTopBeam(energy),
      x: energy,
      shortText: name,
      text: '',
      cssClass: 'custom-annotation',
    };
  }

  private resetEnergyLines() {
    Object.keys(this.calcAnno).forEach((key) => {
      this.calcAnno[key].ka.display = false;
      this.calcAnno[key].kb.display = false;
      this.calcAnno[key].la.display = false;
      this.calcAnno[key].lb.display = false;
    });
  }

  private getEnergyLines() {
    const obj = [];
    Object.keys(this.calcAnno).forEach((key) => {
      obj.push({
        name: key + ' Kα',
        selected: this.calcAnno[key].ka.display,
      });
      obj.push({
        name: key + ' Kß',
        selected: this.calcAnno[key].kb.display,
      });
      obj.push({
        name: key + ' Lα',
        selected: this.calcAnno[key].la.display,
      });
      obj.push({
        name: key + ' Lß',
        selected: this.calcAnno[key].lb.display,
      });
    });
    return obj;
  }

  private updateAnnotations(newAnno) {
    if (this.graphHandle) {
      Object.keys(this.calcAnno).forEach((key) => {
        if (this.calcAnno[key].ka.display) {
          newAnno.push(this.calcAnno[key].ka.anno);
        }
        if (this.calcAnno[key].kb.display) {
          newAnno.push(this.calcAnno[key].kb.anno);
        }
        if (this.calcAnno[key].la.display) {
          newAnno.push(this.calcAnno[key].la.anno);
        }
        if (this.calcAnno[key].lb.display) {
          newAnno.push(this.calcAnno[key].lb.anno);
        }
      });
      this.graphHandle.setAnnotations(newAnno);
    }
  }

  public stopClickPropogation(event: any) {
    event.stopPropagation();
  }

  private updateSettings() {
    if (
      this.displaySettings.id.length === 0 ||
      this.displaySettings.id === '00000000-0000-0000-0000-000000000000'
    ) {
      this.settingsService.addSettings(this.displaySettings).then(
        () => {
          this.updateBlade();
        },
        (error) => {
          console.log('settings insert failed: ', error);
        }
      );
    } else {
      this.settingsService.updateSettings(this.displaySettings).then(
        (res) => {
          this.updateBlade();
        },
        (error) => {
          console.log('settings update failed: ', error);
        }
      );
    }
  }

  public elementFullNames: { [symbol: string]: string } = {
      H: 'Hydrogen', He: 'Helium', Li: 'Lithium', Be: 'Beryllium', B: 'Boron', C: 'Carbon', N: 'Nitrogen', O: 'Oxygen', F: 'Fluorine', Ne: 'Neon', Na: 'Sodium',
      Mg: 'Magnesium', Al: 'Aluminum', Si: 'Silicon', P: 'Phosphorus', S: 'Sulfur', Cl: 'Chlorine', Ar: 'Argon', K: 'Potassium', Ca: 'Calcium', Sc: 'Scandium',
      Ti: 'Titanium', V: 'Vanadium', Cr: 'Chromium', Mn: 'Manganese', Fe: 'Iron', Co: 'Cobalt', Ni: 'Nickel', Cu: 'Copper', Zn: 'Zinc', Ga: 'Gallium',
      Ge: 'Germanium', As: 'Arsenic', Se: 'Selenium', Br: 'Bromine', Kr: 'Krypton', Rb: 'Rubidium', Sr: 'Strontium', Y: 'Yttrium', Zr: 'Zirconium', Nb: 'Niobium',
      Mo: 'Molybdenum', Tc: 'Technetium', Ru: 'Ruthenium', Rh: 'Rhodium', Pd: 'Palladium', Ag: 'Silver', Cd: 'Cadmium', In: 'Indium', Sn: 'Tin', Sb: 'Antimony',
      Te: 'Tellurium', I: 'Iodine', Xe: 'Xenon', Cs: 'Cesium', Ba: 'Barium', La: 'Lanthanum', Ce: 'Cerium', Pr: 'Praseodymium', Nd: 'Neodymium', Pm: 'Promethium',
      Sm: 'Samarium', Eu: 'Europium', Gd: 'Gadolinium', Tb: 'Terbium', Dy: 'Dysprosium', Ho: 'Holmium', Er: 'Erbium', Tm: 'Thulium', Yb: 'Ytterbium',
      Lu: 'Lutetium', Hf: 'Hafnium', Ta: 'Tantalum', W: 'Tungsten', Re: 'Rhenium', Os: 'Osmium', Ir: 'Iridium', Pt: 'Platinum', Au: 'Gold', Hg: 'Mercury',
      Tl: 'Thallium', Pb: 'Lead', Bi: 'Bismuth', Po: 'Polonium', At: 'Astatine', Rn: 'Radon', Fr: 'Francium', Ra: 'Radium', Ac: 'Actinium', Th: 'Thorium',
      Pa: 'Protactinium', U: 'Uranium', Np: 'Neptunium', Pu: 'Plutonium', Am: 'Americium', Cm: 'Curium', Bk: 'Berkelium', Cf: 'Californium', Es: 'Einsteinium',
      Fm: 'Fermium', Md: 'Mendelevium', No: 'Nobelium', Lr: 'Lawrencium', Rf: 'Rutherfordium', Db: 'Dubnium', Sg: 'Seaborgium', Bh: 'Bohrium', Hs: 'Hassium',
      Mt: 'Meitnerium', Ds: 'Darmstadtium', Rg: 'Roentgenium', Cn: 'Copernicium', Nh: 'Nihonium', Fl: 'Flerovium', Mc: 'Moscovium', Lv: 'Livermorium',
      Ts: 'Tennessine', Og: 'Oganesson', LE: 'Light Element'
  };

  public getElementFullName(symbol: string): string {
    const s = (symbol ?? '').trim();
    return this.elementFullNames[s] ?? s;
  }
}
